import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Class } from '../../data/types';
import { getPublishTheme } from '../themes';
import { resolveWidgetOrder } from '../widgetOrder';
import type { PublishConfig } from '../types';

/**
 * Class Quiz template — kid is challenged to "show me X". The page
 * picks a target class at random; live model output scores agreement.
 * On a confident match we celebrate (cheer + emoji burst), then roll
 * a fresh target after a short breather. Builds healthy challenge on
 * top of what Sticker Cam already does.
 *
 * The preview component below simulates the live model: it rotates
 * "matches" so the kid in the composer sees a representative version
 * of the published page. The published page replaces the simulator
 * with the real model output (`liveLabelId`).
 */

export interface ClassQuizProps {
  config: PublishConfig;
  classes: ReadonlyArray<Pick<Class, 'id' | 'name'>>;
  liveLabelId?: string | null;
  /** When the host project is audio, the preview swaps the camera
   *  area for a pulse stage so the kid sees the audio quiz layout. */
  projectKind?: 'image' | 'audio' | 'pose';
}

interface Burst {
  id: number;
  emoji: string;
  x: number;
  y: number;
}

const TARGET_ROUND_MS = 4500; // breather + new target
const PREVIEW_TICK_MS = 1300; // simulated match cadence

export function ClassQuiz({
  config,
  classes,
  liveLabelId = null,
  projectKind = 'image',
}: ClassQuizProps): JSX.Element {
  const isAudio = projectKind === 'audio';
  const theme = getPublishTheme(config.themeId);
  const isSideBy = config.layoutVariant === 1;
  const ids = useMemo(() => classes.map((c) => c.id), [classes]);

  // Game state — only the target / score live in state; bursts are
  // ephemeral and managed via setBursts. Score is "successful matches",
  // streak is consecutive successful rounds without a miss.
  const [targetId, setTargetId] = useState<string | null>(ids[0] ?? null);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [round, setRound] = useState(1);
  const [bursts, setBursts] = useState<Burst[]>([]);
  const burstId = useRef(0);
  const lockedRef = useRef(false);

  // Pick a fresh target distinct from the current one when possible.
  const pickNextTarget = useCallback(() => {
    if (ids.length === 0) {
      setTargetId(null);
      return;
    }
    if (ids.length === 1) {
      setTargetId(ids[0]!);
      return;
    }
    const pool = ids.filter((id) => id !== targetId);
    const next = pool[Math.floor(Math.random() * pool.length)];
    setTargetId(next ?? ids[0]!);
  }, [ids, targetId]);

  const fireBurst = useCallback((emoji: string) => {
    const id = ++burstId.current;
    setBursts((bs) => [
      ...bs,
      {
        id,
        emoji,
        x: 20 + Math.random() * 60,
        y: 20 + Math.random() * 60,
      },
    ]);
    window.setTimeout(() => {
      setBursts((bs) => bs.filter((b) => b.id !== id));
    }, 1200);
  }, []);

  const registerHit = useCallback(
    (matchedId: string) => {
      if (lockedRef.current) return;
      if (matchedId !== targetId) {
        // A different class won — streak break, but don't penalise score.
        setStreak(0);
        return;
      }
      lockedRef.current = true;
      setScore((s) => s + 1);
      setStreak((s) => {
        const next = s + 1;
        setBestStreak((b) => (next > b ? next : b));
        return next;
      });
      const cust = config.perClass[matchedId];
      if (cust?.emoji) fireBurst(cust.emoji);
      window.setTimeout(() => {
        setRound((r) => r + 1);
        pickNextTarget();
        lockedRef.current = false;
      }, 1100);
    },
    [config.perClass, fireBurst, pickNextTarget, targetId],
  );

  // When a real liveLabelId is provided, react to it directly.
  useEffect(() => {
    if (liveLabelId === null || liveLabelId === undefined) return;
    registerHit(liveLabelId);
  }, [liveLabelId, registerHit]);

  // Preview-only: simulate a slow stream of guesses every ~1.3s. We
  // bias towards the target ~55% of the time so the kid sees frequent
  // wins in the composer (rather than bombing every round).
  useEffect(() => {
    if (liveLabelId !== undefined && liveLabelId !== null) return;
    if (ids.length === 0) return;
    const t = window.setInterval(() => {
      const useTarget = Math.random() < 0.55;
      const pickId =
        useTarget && targetId ? targetId : (ids[Math.floor(Math.random() * ids.length)] ?? null);
      if (pickId) registerHit(pickId);
    }, PREVIEW_TICK_MS);
    return () => window.clearInterval(t);
  }, [ids, liveLabelId, registerHit, targetId]);

  // Auto-skip a stale target so the preview never sits stuck.
  useEffect(() => {
    if (liveLabelId !== undefined && liveLabelId !== null) return;
    const id = window.setInterval(() => {
      if (!lockedRef.current) {
        setStreak(0);
        setRound((r) => r + 1);
        pickNextTarget();
      }
    }, TARGET_ROUND_MS);
    return () => window.clearInterval(id);
  }, [liveLabelId, pickNextTarget]);

  const themeStyle: React.CSSProperties = {
    '--bl-pub-bg': theme.bg,
    '--bl-pub-fg': theme.fg,
    '--bl-pub-accent': theme.accent,
    '--bl-pub-accent-fg': theme.accentFg,
    '--bl-pub-secondary': theme.secondary,
    '--bl-pub-surface': theme.surface,
    background: theme.gradient ?? theme.bg,
    color: theme.fg,
  } as React.CSSProperties;

  const target = classes.find((c) => c.id === targetId) ?? null;
  const targetCust = target ? config.perClass[target.id] : null;
  const resolvedOrder = resolveWidgetOrder(config.widgetOrder);

  return (
    <div
      className={`bl-pub-classquiz${isSideBy ? ' bl-pub-classquiz--side' : ''}`}
      style={themeStyle}
    >
      <header className="bl-pub-classquiz__header">
        <h1 className="bl-pub-classquiz__title">{config.text.title}</h1>
        {config.text.subtitle && (
          <p className="bl-pub-classquiz__subtitle">{config.text.subtitle}</p>
        )}
      </header>
      <div className="bl-pub-classquiz__stage">
        <section className="bl-pub-classquiz__target" aria-live="polite">
          <p className="bl-pub-classquiz__eyebrow">Round {round} — show me</p>
          <p className="bl-pub-classquiz__target-emoji" aria-hidden="true">
            {targetCust?.emoji ?? '✨'}
          </p>
          <p className="bl-pub-classquiz__target-name">
            {targetCust?.label ?? target?.name ?? 'a class'}
          </p>
        </section>
        <section
          className={`bl-pub-classquiz__camera${isAudio ? ' bl-pub-classquiz__camera--audio' : ''}`}
          aria-label={isAudio ? 'Microphone placeholder' : 'Camera viewfinder placeholder'}
        >
          {isAudio ? (
            <>
              <span aria-hidden="true" className="bl-pub-classquiz__pulse" />
              <span className="bl-pub-classquiz__camera-text">Live mic</span>
            </>
          ) : (
            <>
              <span aria-hidden="true">📷</span>
              <span className="bl-pub-classquiz__camera-text">Live camera</span>
            </>
          )}
          {bursts.map((b) => (
            <span
              key={b.id}
              className="bl-pub-classquiz__burst"
              style={{ left: `${b.x}%`, top: `${b.y}%` }}
              aria-hidden="true"
            >
              {b.emoji}
            </span>
          ))}
        </section>
      </div>
      <div className="bl-pub-classquiz__scoreboard" aria-label="Score">
        <div>
          <p className="bl-pub-classquiz__score-num">{score}</p>
          <p className="bl-pub-classquiz__score-label">Score</p>
        </div>
        <div>
          <p className="bl-pub-classquiz__score-num">{streak}</p>
          <p className="bl-pub-classquiz__score-label">Streak</p>
        </div>
        <div>
          <p className="bl-pub-classquiz__score-num">{bestStreak}</p>
          <p className="bl-pub-classquiz__score-label">Best</p>
        </div>
      </div>
      {config.text.actionLabel && (
        <button
          type="button"
          className="bl-pub-classquiz__cta"
          onClick={() => {
            setStreak(0);
            setRound((r) => r + 1);
            pickNextTarget();
          }}
        >
          {config.text.actionLabel}
        </button>
      )}
      {/* Optional add-on widgets, in the kid's chosen order. We
          deliberately skip scoreStreak (the template ships its own
          scoreboard) and bigBanner (the target card already plays
          the role of "what is the model seeing"). */}
      {resolvedOrder.map((id) => {
        if (!config.widgets?.[id]) return null;
        if (id === 'scoreStreak' || id === 'bigBanner') return null;
        if (id === 'confidenceBars') {
          return <ConfidenceBarsRow key={id} config={config} classes={classes} />;
        }
        if (id === 'historyStrip') {
          return null; // history strip would conflict with the round counter
        }
        if (id === 'qrCode') {
          return (
            <div key={id} className="bl-pub-classquiz__qr">
              <span className="bl-pub-classquiz__qr-thumb" aria-hidden="true">
                <span>▣</span>
              </span>
              <span>Scan with a phone to try it</span>
            </div>
          );
        }
        if (id === 'shareLinks') {
          return (
            <div key={id} className="bl-pub-classquiz__share" aria-label="Share buttons (preview)">
              <p className="bl-pub-classquiz__share-eyebrow">Show off</p>
              <div className="bl-pub-classquiz__share-row">
                <span className="bl-pub-classquiz__share-btn">
                  <span aria-hidden="true">🔗</span>
                  <span>Copy link</span>
                </span>
                <span className="bl-pub-classquiz__share-btn">
                  <span aria-hidden="true">📤</span>
                  <span>Send</span>
                </span>
                <span className="bl-pub-classquiz__share-btn">
                  <span aria-hidden="true">✉️</span>
                  <span>Email</span>
                </span>
              </div>
            </div>
          );
        }
        return null;
      })}
      {config.branding.showFooter && (
        <footer className="bl-pub-classquiz__footer">
          Made on <strong>Bryta</strong>
        </footer>
      )}
    </div>
  );
}

function ConfidenceBarsRow({
  config,
  classes,
}: {
  config: PublishConfig;
  classes: ReadonlyArray<Pick<Class, 'id' | 'name'>>;
}): JSX.Element {
  const [probs, setProbs] = useState<number[]>(() =>
    Array(classes.length).fill(1 / Math.max(1, classes.length)),
  );
  useEffect(() => {
    if (classes.length === 0) return;
    const id = window.setInterval(() => {
      const next = Array.from({ length: classes.length }, () => Math.random());
      const s = next.reduce((a, b) => a + b, 0);
      setProbs(next.map((v) => v / s));
    }, 1500);
    return () => window.clearInterval(id);
  }, [classes.length]);
  return (
    <ul className="bl-pub-classquiz__bars" aria-label="Confidence bars">
      {classes.map((c, i) => {
        const cust = config.perClass[c.id];
        const pct = Math.round((probs[i] ?? 0) * 100);
        return (
          <li key={c.id} className="bl-pub-classquiz__bar">
            <span className="bl-pub-classquiz__bar-name">{cust?.label ?? c.name}</span>
            <span className="bl-pub-classquiz__bar-track">
              <span className="bl-pub-classquiz__bar-fill" style={{ width: `${pct}%` }} />
            </span>
            <span className="bl-pub-classquiz__bar-pct">{pct}%</span>
          </li>
        );
      })}
    </ul>
  );
}
